import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import {
  generateKeyPair,
  encryptPrivateKey,
  encodePublicKey,
  deriveKeyFromPassword,
} from "@/lib/crypto";
import { useAuthStore } from "@/store/authStore";
import type { AuthResponse } from "@snowcord/shared";
import "./AuthPages.css";

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await api.post<AuthResponse>("/auth/login", {
        username,
        password,
      });

      const salt = Uint8Array.from(atob(res.privateKeySalt), (c) => c.charCodeAt(0));
      const derivedKey = await deriveKeyFromPassword(password, salt);

      const encryptedPrivKey = localStorage.getItem("encryptedPrivateKey");
      const privKeyIv = localStorage.getItem("privateKeyIv");

      let privateKey: Uint8Array | null = null;
      if (encryptedPrivKey && privKeyIv) {
        try {
          privateKey = await decryptPrivateKeyLocal(encryptedPrivKey, privKeyIv, derivedKey);
        } catch {
          setError("Failed to decrypt private key. You may need to re-register.");
        }
      }

    login(res.user, res.tokens, privateKey!);
    localStorage.setItem("refreshToken", res.tokens.refreshToken);
    navigate("/app/dm");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-page__container">
        <h1 className="auth-page__wordmark">Snowcord</h1>
        <p className="auth-page__subtitle">Welcome back. Sign in to continue.</p>

        {error && <div className="auth-page__form-error">{error}</div>}

        <form className="auth-page__form" onSubmit={handleSubmit}>
          <div className="auth-page__field">
            <label className="auth-page__label" htmlFor="login-username">
              Username
            </label>
            <input
              id="login-username"
              className="auth-page__input"
              type="text"
              placeholder="atlas"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div className="auth-page__field">
            <label className="auth-page__label" htmlFor="login-password">
              Password
            </label>
            <div className="auth-page__password-wrapper">
              <input
                id="login-password"
                className="auth-page__input"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                className="auth-page__password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            className="auth-page__submit"
            type="submit"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <div className="auth-page__footer">
          Don't have an account? <Link to="/register">Create one</Link>
        </div>
      </div>
    </div>
  );
}

async function decryptPrivateKeyLocal(
  encrypted: string,
  iv: string,
  derivedKey: CryptoKey,
): Promise<Uint8Array> {
  const encryptedBytes = Uint8Array.from(atob(encrypted), (c) => c.charCodeAt(0));
  const ivBytes = Uint8Array.from(atob(iv), (c) => c.charCodeAt(0));
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ivBytes },
    derivedKey,
    encryptedBytes,
  );
  return new Uint8Array(decrypted);
}

export function RegisterPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);

    try {
      const keyPair = generateKeyPair();
      const publicKeyB64 = encodePublicKey(keyPair.publicKey);

      const res = await api.post<AuthResponse>("/auth/register", {
        username,
        displayName,
        password,
        publicKey: publicKeyB64,
      });

      const saltStr = res.privateKeySalt;
      const salt = Uint8Array.from(atob(saltStr), (c) => c.charCodeAt(0));
      const derivedKey = await deriveKeyFromPassword(password, salt);

      const { encrypted, iv } = await encryptPrivateKey(
        keyPair.privateKey,
        derivedKey,
      );

      localStorage.setItem("encryptedPrivateKey", encrypted);
      localStorage.setItem("privateKeyIv", iv);
      localStorage.setItem("privateKeySalt", saltStr);
      localStorage.setItem("refreshToken", res.tokens.refreshToken);

    login(res.user, res.tokens, keyPair.privateKey);
    navigate("/app/dm");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError("Username is already taken");
        } else {
          setError(err.message);
        }
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-page__container">
        <h1 className="auth-page__wordmark">Snowcord</h1>
        <p className="auth-page__subtitle">Create your account to get started.</p>

        {error && <div className="auth-page__form-error">{error}</div>}

        <form className="auth-page__form" onSubmit={handleSubmit}>
          <div className="auth-page__field">
            <label className="auth-page__label" htmlFor="reg-display">
              Display Name
            </label>
            <input
              id="reg-display"
              className="auth-page__input"
              type="text"
              placeholder="Atlas"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>

          <div className="auth-page__field">
            <label className="auth-page__label" htmlFor="reg-username">
              Username
            </label>
            <input
              id="reg-username"
              className="auth-page__input"
              type="text"
              placeholder="atlas"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>

          <div className="auth-page__field">
            <label className="auth-page__label" htmlFor="reg-password">
              Password
            </label>
            <div className="auth-page__password-wrapper">
              <input
                id="reg-password"
                className="auth-page__input"
                type={showPassword ? "text" : "password"}
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                className="auth-page__password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="auth-page__field">
            <label className="auth-page__label" htmlFor="reg-confirm">
              Confirm Password
            </label>
            <input
              id="reg-confirm"
              className="auth-page__input"
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          <button
            className="auth-page__submit"
            type="submit"
            disabled={loading}
          >
            {loading ? "Creating account..." : "Create account"}
          </button>
        </form>

        <div className="auth-page__footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
